"use client";

/**
 * Hoisted once per render (never inside a `.map()` callback, which would break
 * the Rules of Hooks) so every ActivityCard/TimeSlotPicker in the section reads
 * from the same precomputed maps instead of each calling its own hook.
 */

import { useMemo } from "react";
import type { ActivityRow, ActivityTimeSlotRow } from "../_logic/types/db-types";
import { evaluateRuleBlock, type RuleBlockReason, type SignupLimitRules } from "../_logic/utils/activity-signup-status";

interface UseActivityRuleStateArgs {
  activities: ActivityRow[];
  timeSlotsByActivity: Map<number, ActivityTimeSlotRow[]>;
  /** Activities the viewer holds a `registered` (not waitlisted) row for in this event. */
  registeredActivityIds: Set<number>;
  /** date -> count of the viewer's own `registered` rows on that date. */
  registeredCountByDate: Map<string, number>;
  rules: SignupLimitRules;
}

export interface ActivityRuleState {
  /** Per-slot block reason — drives TimeSlotPicker's trailing "Blocked" label. */
  bySlotId: Map<number, RuleBlockReason>;
  /** Per-activity block reason, set only when every bookable slot is blocked — drives ActivityCard's blocked state. */
  byActivityId: Map<number, RuleBlockReason>;
}

const EMPTY_RULE_STATE: ActivityRuleState = { bySlotId: new Map(), byActivityId: new Map() };

/**
 * Precomputes the day-cap/exclusion-group client-side hint for every bookable slot of every
 * activity in the section. Pure hint only — see evaluateRuleBlock's docblock.
 *
 * @param args - Activities/slots to evaluate + the viewer's own registrations + the event's rules.
 * @returns Per-slot and per-activity block-reason lookups.
 */
export function useActivityRuleState({
  activities,
  timeSlotsByActivity,
  registeredActivityIds,
  registeredCountByDate,
  rules,
}: UseActivityRuleStateArgs): ActivityRuleState {
  return useMemo(() => {
    // Common case: no limits configured for this event (or builder mode, where rules always
    // stays empty) — skip the full scan entirely.
    if (rules.dayCapsByDate.size === 0 && rules.exclusionGroups.length === 0) return EMPTY_RULE_STATE;

    const bySlotId = new Map<number, RuleBlockReason>();
    const byActivityId = new Map<number, RuleBlockReason>();

    for (const activity of activities) {
      const bookableSlots = (timeSlotsByActivity.get(activity.id) ?? []).filter((s) => s.is_bookable);
      if (bookableSlots.length === 0) continue;

      // The exclusion-group half of evaluateRuleBlock doesn't depend on slot date, so it's the
      // same result for every slot of this activity — evaluate it once, not once per slot.
      const activityBlock = evaluateRuleBlock(activity.id, null, registeredActivityIds, registeredCountByDate, rules);

      let firstBlock: RuleBlockReason | null = null;
      let allBlocked = true;
      for (const slot of bookableSlots) {
        const block =
          activityBlock ??
          evaluateRuleBlock(activity.id, slot.date, registeredActivityIds, registeredCountByDate, rules);
        if (block) {
          bySlotId.set(slot.id, block);
          firstBlock ??= block;
        } else {
          allBlocked = false;
        }
      }
      if (allBlocked && firstBlock) {
        byActivityId.set(activity.id, firstBlock);
      }
    }

    return { bySlotId, byActivityId };
  }, [activities, timeSlotsByActivity, registeredActivityIds, registeredCountByDate, rules]);
}
